import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Club } from '../types';
import { isClubAdmin, isSuperAdmin } from '../utils/roleUtils';
import { ROLE_DASHBOARD_MAP } from '../constants/roles';
import { CloseIcon, FolderIcon } from './Icons';
import { cache } from '../utils/cache';
import { invalidateClubsCache } from '../utils/cacheInvalidation';
import { uploadImageToCdn } from '../lib/uploadImage';
import { BACKEND_URL } from '../lib/backendUrl';

const API_URL = BACKEND_URL;

const toClubArray = (payload: any): Club[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.clubs)) return payload.clubs;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const ClubsPage: React.FC = () => {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateClub, setShowCreateClub] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ show: boolean; clubId: string | null; clubName: string }>({
    show: false,
    clubId: null,
    clubName: ''
  });
  const { token, user } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  useEffect(() => {
    console.log('ClubsPage - User role:', user?.role);
    console.log('ClubsPage - Is club admin?', isClubAdmin(user?.role));
    console.log('ClubsPage - Is super admin?', isSuperAdmin(user?.role));
    console.log('ClubsPage - Show create button?', isClubAdmin(user?.role) || isSuperAdmin(user?.role));
  }, [user?.role]);

  const [clubForm, setClubForm] = useState({ name: '', description: '', image: '' });

  useEffect(() => {
    fetchClubs();
  }, [user?.id, user?.role]);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      // Generate cache key based on user role and authentication
      const cacheKey = token 
        ? `clubs_${user?.role}_${user?.id}` 
        : 'clubs_public';
      
      // Use cache only for public listing. Authenticated dashboards should always reflect live DB.
      if (!token) {
        const cachedData = cache.get<Club[]>(cacheKey);
        if (cachedData) {
          console.log('Using cached clubs data');
          setClubs(cachedData);
          setLoading(false);
          return;
        }
      }

      console.log('Fetching clubs from API');
      // If not logged in, use public endpoint; otherwise use protected endpoint
      const endpoint = token ? `${API_URL}/api/clubs` : `${API_URL}/public/clubs`;
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      
      const response = await axios.get(endpoint, config);
      const clubList = toClubArray(response.data);

      console.log('Fetched clubs from API:', clubList.length);
      
      setClubs(clubList);
      // Cache public listing for 5 minutes.
      if (!token) {
        cache.set(cacheKey, clubList, 5 * 60 * 1000);
      }
    } catch (error) {
      console.error('Failed to fetch clubs', error);
      setClubs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClub = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/clubs`, clubForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowCreateClub(false);
      setClubForm({ name: '', description: '', image: '' });
      setImagePreview('');
      // Clear cache to force refresh
      invalidateClubsCache(user);
      fetchClubs();
    } catch (error) {
      console.error('Failed to create club', error);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const uploadedUrl = await uploadImageToCdn(file, token, 'clubs');
        setImagePreview(uploadedUrl);
        setClubForm({ ...clubForm, image: uploadedUrl });
      } catch (error: any) {
        alert(error?.message || 'Failed to upload image');
      }
    }
  };

  const handleDeleteClick = (clubId: string, clubName: string) => {
    setDeleteConfirmation({
      show: true,
      clubId,
      clubName
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmation.clubId) return;
    
    try {
      await axios.delete(`${API_URL}/api/clubs/${deleteConfirmation.clubId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteConfirmation({ show: false, clubId: null, clubName: '' });
      // Clear cache to force refresh
      invalidateClubsCache(user);
      fetchClubs();
    } catch (error: any) {
      const errorMessage = error.response?.data || error.message || 'Failed to delete club';
      console.error('Failed to delete club:', errorMessage);
      alert(`Error: ${typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)}`);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ show: false, clubId: null, clubName: '' });
  };

  const handleBackToDashboard = () => {
    if (user?.role) {
      const normalizedRole = user.role.toUpperCase().replace('-', '_') as keyof typeof ROLE_DASHBOARD_MAP;
      const dashboardPath = ROLE_DASHBOARD_MAP[normalizedRole];
      navigate(dashboardPath || '/');
    } else {
      navigate('/');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dashboard-content">
      {/* Back Button */}
      <motion.button
        onClick={handleBackToDashboard}
        className="back-button"
        style={{ marginLeft: '5rem' }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        ← BACK
      </motion.button>

      <h2 className="dashboard-title" style={{ fontSize: '3.5rem' }}>CLUBS</h2>

      {(isClubAdmin(user?.role) || isSuperAdmin(user?.role)) && (
        <div className="admin-actions">
          <button onClick={() => setShowCreateClub(true)} className="cyber-button">
            CREATE NEW CLUB
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="cards-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="futuristic-card" style={{ minHeight: '380px' }}>
              <div className="card-background" style={{ background: 'linear-gradient(90deg, rgba(139,92,246,0.1) 0%, rgba(139,92,246,0.2) 50%, rgba(139,92,246,0.1) 100%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }}></div>
              <div className="card-content">
                <div style={{ height: '20px', background: 'rgba(139,92,246,0.2)', borderRadius: '4px', marginBottom: '10px', animation: 'pulse 1.5s infinite' }}></div>
                <div style={{ height: '60px', background: 'rgba(139,92,246,0.1)', borderRadius: '4px', animation: 'pulse 1.5s infinite 0.2s' }}></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateClub && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          className="modal-overlay" 
          onClick={() => setShowCreateClub(false)}
        >
          <motion.div 
            initial={{ scale: 0.8, y: 50, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.8, y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="modal-content create-club-modal" 
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div 
              className="modal-header"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="modal-title">CREATE NEW CLUB</h3>
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="modal-close-btn"
                onClick={() => setShowCreateClub(false)}
                type="button"
              >
                <CloseIcon size={14} />
              </motion.button>
            </motion.div>

            <form onSubmit={handleCreateClub} className="create-club-form">
              <motion.div 
                className="form-group"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <label className="form-label">
                  Club Name
                </label>
                <input
                  type="text"
                  placeholder="Enter club name..."
                  value={clubForm.name}
                  onChange={(e) => setClubForm({ ...clubForm, name: e.target.value })}
                  className="cyber-input enhanced-input"
                  required
                />
              </motion.div>

              <motion.div 
                className="form-group"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <label className="form-label">
                  Description
                </label>
                <textarea
                  placeholder="Describe your club's mission and activities..."
                  value={clubForm.description}
                  onChange={(e) => setClubForm({ ...clubForm, description: e.target.value })}
                  className="cyber-textarea enhanced-textarea"
                  rows={4}
                  required
                />
              </motion.div>

              <motion.div 
                className="form-group"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <label className="form-label">
                  Club Image
                </label>
                <div className="file-upload-container">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="file-input"
                    id="club-image-upload"
                  />
                  <label htmlFor="club-image-upload" className="file-upload-label">
                    <span className="upload-icon"><FolderIcon size={18} /></span>
                    <span>{imagePreview ? 'Change Image' : 'Choose Image'}</span>
                  </label>
                  <p className="file-hint">Max size: 5MB | Formats: JPG, PNG, GIF</p>
                </div>
                {imagePreview && (
                  <motion.div 
                    className="image-preview"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <img src={imagePreview} alt="Preview" />
                    <button 
                      type="button" 
                      className="remove-image-btn"
                      onClick={() => {
                        setImagePreview('');
                        setClubForm({ ...clubForm, image: '' });
                      }}
                    >
                      <CloseIcon size={12} />
                    </button>
                  </motion.div>
                )}
              </motion.div>

              <motion.div 
                className="modal-buttons"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <motion.button 
                  type="submit" 
                  className="cyber-button create-btn"
                  whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(255, 0, 110, 0.6)" }}
                  whileTap={{ scale: 0.95 }}
                >
                  CREATE CLUB
                </motion.button>
                <motion.button 
                  type="button" 
                  onClick={() => setShowCreateClub(false)} 
                  className="cyber-button-secondary cancel-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  CANCEL
                </motion.button>
              </motion.div>
            </form>
          </motion.div>
        </motion.div>
      )}

      {deleteConfirmation.show && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          className="modal-overlay" 
          onClick={handleCancelDelete}
        >
          <motion.div 
            initial={{ scale: 0.8, y: 50, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.8, y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="modal-content delete-confirmation-modal" 
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div 
              className="modal-header"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="modal-title">DELETE CLUB</h3>
            </motion.div>

            <motion.div 
              className="confirmation-content"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <p className="confirmation-message">
                Are you sure you want to delete <span className="club-name-highlight">{deleteConfirmation.clubName}</span>?
              </p>
              <p className="confirmation-warning">This action cannot be undone.</p>
            </motion.div>

            <motion.div 
              className="modal-buttons confirmation-buttons"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <motion.button 
                type="button" 
                onClick={handleConfirmDelete}
                className="cyber-button delete-btn"
                whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(255, 50, 50, 0.6)" }}
                whileTap={{ scale: 0.95 }}
              >
                DELETE
              </motion.button>
              <motion.button 
                type="button" 
                onClick={handleCancelDelete} 
                className="cyber-button-secondary cancel-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                CANCEL
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}

      {!loading && (
        <div className="cards-grid">
          {clubs.map((club) => (
            <motion.div 
              key={club.id} 
              whileHover={{ scale: 1.05 }} 
              className="futuristic-card"
            >
            {/* Card Background Image */}
            {club.image && (
              <div className="card-background">
                <img src={club.image} alt={club.name} loading="lazy" />
                <div className="card-image-overlay"></div>
              </div>
            )}
            
            {/* Card Border Accent */}
            <div className="card-border-accent"></div>
            
            {/* Card Content */}
            <div className="card-content">
              {/* Vertical Stripe */}
              <div className="card-vertical-stripe"></div>
              
              {/* Club Name */}
              <h4 className="futuristic-card-title">{club.name}</h4>
              
              {/* Club Description */}
              <p className="futuristic-card-description">{club.description}</p>
              
              {/* Card Footer with Button */}
              <div className="card-footer">
                <motion.button
                  className="card-enter-btn"
                  onClick={() => {
                    // Navigate to the correct club path based on current context
                    const currentPath = window.location.pathname;
                    if (currentPath.includes('/dashboard/club')) {
                      navigate(`/dashboard/club/clubs/${club.id}`);
                    } else if (currentPath.includes('/dashboard/student')) {
                      navigate(`/dashboard/student/clubs/${club.id}`);
                    } else if (currentPath.includes('/dashboard/admin')) {
                      navigate(`/dashboard/admin/clubs/${club.id}`);
                    } else {
                      navigate(`/public/clubs/${club.id}`);
                    }
                  }}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: "0 0 30px rgba(0, 255, 255, 0.8)"
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  ENTER
                </motion.button>
                <motion.button
                  className="card-join-gc-btn"
                  onClick={() => {
                    const currentPath = window.location.pathname;
                    if (currentPath.includes('/dashboard/club')) {
                      navigate(`/dashboard/club/clubs/${club.id}?tab=community`);
                    } else if (currentPath.includes('/dashboard/student')) {
                      navigate(`/dashboard/student/clubs/${club.id}?tab=community`);
                    } else if (currentPath.includes('/dashboard/admin')) {
                      navigate(`/dashboard/admin/clubs/${club.id}?tab=community`);
                    } else {
                      navigate(`/public/clubs/${club.id}?tab=community`);
                    }
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  JOIN GC
                </motion.button>
                {(isSuperAdmin(user?.role) || (isClubAdmin(user?.role) && club.created_by === user?.id)) && (
                  <motion.button
                    className="card-delete-btn"
                    onClick={() => handleDeleteClick(club.id, club.name)}
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: "0 0 30px rgba(255, 50, 50, 0.8)"
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    DELETE
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      )}
    </motion.div>
  );
};

export default ClubsPage;
